import { StatIcon } from "../../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../../ui/Icon.js";
import { useEffect } from "react";
import { useT } from "../../i18n/index.js";
import { navigate } from "../../router.js";
import { Button } from "../../ui/Button.js";
import { formatCompact } from "../../ui/Counter.js";
import { Timer } from "../../ui/Timer.js";
import { api } from "../../api/http.js";
import { useUnits } from "../../units/state.js";
import { useBase } from "../state.js";

import type { TrainingState } from "../../units/state.js";
import { DrillPanel } from "../ColonyPanels.js";

/**
 * Purpose-built sections inside the building sheet (20-base.md R10): each unit building shows its own
 * live numbers and opens its screen instead of a generic info panel.
 */
export function PurposePanel({ panel, level }: { panel: string; level: number }) {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const banners = useUnits((s) => s.banners);
  const arena = useUnits((s) => s.arena);
  const expeditions = useUnits((s) => s.expeditions);
  const refreshRoster = useUnits((s) => s.refreshRoster);
  const refreshWorkshop = useUnits((s) => s.refreshWorkshop);
  const refreshArena = useUnits((s) => s.refreshArena);
  const refreshExpeditions = useUnits((s) => s.refreshExpeditions);
  const closeBase = () => useBase.getState().setPanel({ kind: "none" });
  const go = (to: string) => {
    closeBase();
    navigate(to);
  };

  useEffect(() => {
    if (["workshop", "hangar", "repair", "armory", "command", "trainer"].includes(panel)) void refreshRoster();
    if (panel === "workshop") void refreshWorkshop();
    if (panel === "arena") void refreshArena();
    if (panel === "expedition") void refreshExpeditions();
  }, [panel, refreshRoster, refreshWorkshop, refreshArena, refreshExpeditions]);

  switch (panel) {
    case "workshop": {
      const std = banners?.find((b) => b.id === "standard");
      return (
        <div className="section" data-testid="purpose-workshop">
          <h3>{t("purpose.workshop")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {std ? <span className={`badge ${std.free?.available ? "ok" : ""}`}>{std.free?.available ? t("workshop.free") : t("workshop.freeUsed.day")}</span> : null}
            {std ? <span className="badge gold">{t("workshop.pity", { n: std.pity.sinceS, max: std.pity.hardPity })}</span> : null}
            {std ? <span className="badge">FAB {std.tokensHave}</span> : null}
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/units/workshop")} data-testid="purpose-open">
            {t("purpose.openWorkshop")}
          </Button>
        </div>
      );
    }
    case "hangar":
      return (
        <div className="section" data-testid="purpose-hangar">
          <h3>{t("purpose.hangar")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge">{t("units.count", { n: roster?.units.length ?? 0 })}</span>
            <span className="badge gold">{t("units.abilityCap", { n: level })} <FunctionalIcon name="arrowRight" /> {roster?.caps.abilityCap ?? 1}</span>
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/units")} data-testid="purpose-open">
            {t("purpose.openRoster")}
          </Button>
        </div>
      );
    case "repair":
      return (
        <div className="section" data-testid="purpose-repair">
          <h3>{t("purpose.repair")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge gold">
              {t("repair.slots")} {roster?.repair.used ?? 0} / {roster?.repair.slots ?? 1}
            </span>
            <span className="badge warn"><FunctionalIcon name="close" /> {roster?.units.filter((u) => u.wrecked).length ?? 0}</span>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Button variant="primary" onClick={() => go("/app/units/repair")} data-testid="purpose-open">
              {t("purpose.openRepair")}
            </Button>
          </div>
        </div>
      );
    case "armory":
      return (
        <div className="section" data-testid="purpose-armory">
          <h3>{t("purpose.armory")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge gold">
              {t("armory.queue")} {roster?.craft.used ?? 0} / {roster?.craft.slots ?? 0}
            </span>
            <span className="badge">{t("armory.maxTier", { tier: roster?.craft.maxTier ?? "D" })}</span>
            {roster?.craft.queue[0] ? (
              <span className="badge">
                <FunctionalIcon name="clock" /> <Timer finishesAt={roster.craft.queue[0].finishesAt} />
              </span>
            ) : null}
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/units/armory")} data-testid="purpose-open">
            {t("purpose.openArmory")}
          </Button>
        </div>
      );
    case "command":
      return (
        <div className="section" data-testid="purpose-command">
          <h3>{t("purpose.command")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge gold">
              {t("units.squads")} {roster?.squads.length ?? 0} / {roster?.maxSquads ?? 1}
            </span>
            {roster?.squads.map((s) => (
              <span key={s.slot} className={`badge ${s.garrison ? "ok" : ""}`}>
                {s.name} <StatIcon stat="power" />{formatCompact(s.power)}
              </span>
            ))}
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/units/squads")} data-testid="purpose-open">
            {t("purpose.openSquads")}
          </Button>
        </div>
      );
    case "arena":
      return (
        <div className="section" data-testid="purpose-arena">
          <h3>{t("purpose.arena")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {arena ? <span className="badge gold">{arena.rating} · {arena.band.id}</span> : null}
            {arena ? (
              <span className="badge">
                {t("arena.attempts")} {arena.attempts.max - arena.attempts.used} / {arena.attempts.max}
              </span>
            ) : null}
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/arena")} data-testid="purpose-open">
            {t("purpose.openArena")}
          </Button>
        </div>
      );
    case "expedition":
      return (
        <div className="section" data-testid="purpose-expedition">
          <h3>{t("purpose.expedition")}</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {expeditions ? (
              <span className="badge gold">
                <StatIcon stat="stamina" /> {expeditions.stamina.current} / {expeditions.stamina.cap}
              </span>
            ) : null}
            {expeditions ? <span className="badge"><StatIcon stat="star" /> {expeditions.chapters.reduce((s, c) => s + c.stars, 0)}</span> : null}
          </div>
          <Button variant="primary" style={{ marginTop: 8 }} onClick={() => go("/app/expeditions")} data-testid="purpose-open">
            {t("purpose.openExpeditions")}
          </Button>
        </div>
      );
    case "trainer":
      return <TrainerSection />;
    default:
      return null;
  }
}

/** Training Simulator: XP pools per hour and is claimed as Training Data items (R21 sources). */
function TrainerSection() {
  const t = useT();
  const run = useUnits((s) => s.run);
  const base = useBase((s) => s.base);
  const training = useTraining(base?.enclave.hqLevel ?? 0);
  return (
    <div className="section" data-testid="purpose-trainer">
      <h3>{t("purpose.trainer")}</h3>
      {training ? (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="badge gold">+{training.xpPerHour} XP/h</span>
          <span className="badge">{t("trainer.pooled", { n: formatCompact(training.pooled) })}</span>
          <span className="hint">{t("trainer.cap", { h: training.capHours })}</span>
        </div>
      ) : null}
      <Button variant="primary" style={{ marginTop: 8 }} disabled={!training || training.items <= 0} onClick={() => void run(() => api("POST", "/api/units/training/claim"), t("trainer.toast"))} data-testid="trainer-claim">
        {t("trainer.claim", { n: training?.items ?? 0 })}
      </Button>
      <DrillPanel />
    </div>
  );
}

function useTraining(dep: number): TrainingState | null {
  const roster = useUnits((s) => s.roster);
  const training = useUnits((s) => s.training);
  const refreshTraining = useUnits((s) => s.refreshTraining);
  useEffect(() => {
    void refreshTraining();
  }, [dep, roster?.units.length, refreshTraining]);
  return training;
}
